import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

interface Routine {
  id: string;
  title: string;
  description: string;
}

const HomePage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isStarting, setIsStarting] = useState(false);
  const [showPopup, setShowPopup] = useState(false);


  // 팝업 표시 함수
  const showComingSoonPopup = () => {
    setShowPopup(true);
    setTimeout(() => {
      setShowPopup(false);
    }, 2000);
  };

  // 기본 루틴 데이터
  const defaultRoutines: Routine[] = [
    {
      id: 'morning',
      title: t('home.morning.routine'),
      description: t('home.morning.description')
    },
    {
      id: 'focus',
      title: t('home.focus.routine'),
      description: t('home.focus.description')
    },
    {
      id: 'evening',
      title: t('home.evening.routine'),
      description: t('home.evening.description')
    }
  ];

  // 기존 루틴 시작 함수
  const handleStartRoutine = async (routine: Routine) => {
    // 이미 시작 중이면 무시
    if (isStarting) return;
    
    setIsStarting(true);
    
    // 루틴에 따른 배경음악 설정
    let backgroundMusic = '배경음 1'; // 기본값
    switch (routine.id) {
      case 'morning':
        backgroundMusic = '배경음 1';
        break;
      case 'pocotalk':
        backgroundMusic = '배경음 3';
        break;
      case 'evening':
        backgroundMusic = '배경음 2';
        break;
    }

    // 3초 후 채팅 페이지로 이동
    setTimeout(() => {
      navigate('/chat', { 
        state: { 
          routineId: routine.id,
          routineTitle: routine.title,
          routineDescription: routine.description,
          backgroundMusic: backgroundMusic
        }
      });
    }, 3000);
  };

  // 기존 쏘울 시작 함수 (주석처리)
  const handleStartSoul = async () => {
    navigate('/chat');
  };

  return (
    <div className="bg-gradient-to-br from-green-50 via-white to-blue-50">
      {/* 준비중 팝업 */}
      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-sm mx-4 text-center">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              {t('popup.coming.soon.title')}
            </h3>
            <p className="text-sm text-gray-600">
              {t('popup.coming.soon.description')}
            </p>
          </div>
        </div>
      )}

      {/* 메인 콘텐츠 */}
      <div className="pb-10">
        <div className="max-w-4xl mx-auto px-5">
          {/* 로고 */}
          <div className="text-center mb-6">
            <img 
              src="/images/ic_logo.png" 
              alt="PocoTalk Logo" 
              className="w-52 h-auto mx-auto"
            />
          </div>

          {/* 메인 타이틀 */}
          <div className="text-center mb-5">
            <h1 className="text-xl font-bold text-gray-900 mb-3">
              <span className="text-green-600">{t('home.main.title')}</span>
            </h1>            
          </div>

          {/* 메인 액션 버튼 */}
          <div className="text-center mb-10">
            <button
              onClick={handleStartSoul}
              className="group relative inline-flex items-center justify-center bg-white hover:bg-gray-50 text-gray-900 font-bold py-6 px-6 rounded-2xl transition-all duration-300 hover:shadow-3xl transform hover:scale-105 text-xl border-2 border-gray-200"
            >
              <div className="flex items-center space-x-5">
                <div className="text-left">
                  <div className="text-xl font-bold">{t('home.soul.title')}</div>
                </div>
                <svg className="w-6 h-6 transition-transform group-hover:translate-x-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          </div>

          {!isStarting ? (
            <div className="max-w-xl mx-auto">
              {/* 섹션 타이틀 */}
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  {t('home.recommended.subtitle')}
                </h2>
              </div>

              {/* 루틴 카드들 */}
              <div className="grid gap-5">
                {defaultRoutines.map((routine) => (
                  <div key={routine.id} className="bg-white rounded-2xl border-2 border-gray-200 py-3 px-5 transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {routine.title}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {routine.description}
                        </p>
                      </div>
                      <button
                        onClick={() => handleStartRoutine(routine)}
                        className="bg-mint hover:bg-mint/80 text-white font-bold py-3 px-4 rounded-xl transition-colors duration-200 shadow-lg hover:shadow-xl text-sm"
                      >
                        {t('home.start')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-md mx-auto text-center">
              <div className="bg-white rounded-2xl shadow-lg p-10">
                <div className="text-5xl mb-5">🎉</div>
                <h2 className="text-xl font-bold text-gray-900 mb-3">
                  {t('home.routine.start')}
                </h2>
                <p className="text-sm text-gray-600 mb-6">
                  {t('home.routine.preparing')}
                </p>
                <div className="flex justify-center">
                  <div className="flex space-x-1.5">
                    <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-bounce"></div>
                    <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-5">
                  {t('home.wait')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomePage; 